import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, Divider, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverHeader, PopoverTrigger, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { translate } from '../../../i18n';
import { CourtDto } from '../../../models/court/dto/court.dto';

type Props = {
    courts: CourtDto[];
    selectedCourtIds: number[];
    onChange: (courtIds: number[]) => void;
};

export default function MultiCourtPicker({ courts, selectedCourtIds, onChange }: Props) {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = (courtId: number) => {
        const next = selectedCourtIds.includes(courtId)
            ? selectedCourtIds.filter(id => id !== courtId)
            : [...selectedCourtIds, courtId];
        onChange(next);
    };

    const allSelected = courts.length > 0 && selectedCourtIds.length === courts.length;
    const someSelected = selectedCourtIds.length > 0 && !allSelected;

    const label = selectedCourtIds.length === 0
        ? translate('analytics.filters.all_courts')
        : selectedCourtIds.length > 3
            ? `${selectedCourtIds.length} ${translate('analytics.filters.courts')}`
            : courts
                .filter(c => selectedCourtIds.includes(c.id))
                .map(c => c.name)
                .join(', ');

    return (
        <Popover isOpen={isOpen} onClose={() => setIsOpen(false)}>
            <PopoverTrigger>
                <Button
                    onClick={() => setIsOpen(true)}
                    variant='outline'
                    size='sm'
                    rightIcon={<ChevronDownIcon />}
                    fontWeight='600'
                    fontSize='sm'
                    width='180px'
                    justifyContent='space-between'
                >
                    <Box as='span' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
                        {label}
                    </Box>
                </Button>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight='600'>{translate('analytics.filters.courts')}</PopoverHeader>
                <PopoverBody maxH='300px' overflowY='auto'>
                    <VStack align='start'>
                        <Checkbox
                            isChecked={allSelected}
                            isIndeterminate={someSelected}
                            isDisabled={courts.length === 0}
                            onChange={() => onChange(allSelected ? [] : courts.map(c => c.id))}
                            fontWeight='600'
                        >
                            {allSelected
                                ? translate('analytics.filters.deselect_all')
                                : translate('analytics.filters.select_all')}
                        </Checkbox>
                        <Divider />
                        {courts.map(court => (
                            <Checkbox
                                key={court.id}
                                isChecked={selectedCourtIds.includes(court.id)}
                                onChange={() => handleToggle(court.id)}
                            >
                                {court.name}
                            </Checkbox>
                        ))}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
